'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import config from '@haaretz/l-config';
import Button from '@haaretz/s-button';
import EmailField from '@haaretz/s-form-fields/EmailField';
import PhoneField from '@haaretz/s-form-fields/PhoneField';
import Icon from '@haaretz/s-icon';
import PpLandingPageLayout from '@haaretz/s-pp-landing-page-layout';
import TextField from '@haaretz/s-text-field';
import TextLink from '@haaretz/s-text-link';
import useBi from '@haaretz/s-use-bi';
import React from 'react';
import s9 from 'style9';
import { ErrorImage, SuccessImage } from './utils/Images';
const maxWidthTitleSmallToLarge = '546px';
const maxWidthTitleFromLarge = '720px';
const c = s9.create({
    base: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center'
    },
    title: {
        marginTop: "0.5rem",
        fontSize: "var(--fz-5)",
        lineHeight: "var(--lh-5)",
        "@media all and (max-width:37.4375em)": {
            paddingRight: "1.5rem",
            paddingLeft: "1.5rem",
            textAlign: 'center'
        },
        "@media all and (min-width:37.5em) and (max-width:63.9375em)": {
            maxWidth: '546px'
        },
        "@media all and (min-width:64em)": {
            marginTop: "0rem",
            maxWidth: '720px',
            fontSize: "var(--fz-6)",
            lineHeight: "var(--lh-6)"
        },
        "@media all and (min-width:80em) and (max-width:101.9375em)": {
            fontSize: "var(--fz-8)",
            lineHeight: "var(--lh-8)"
        },
        "@media all and (min-width:102em)": {
            fontSize: "var(--fz-7)",
            lineHeight: "var(--lh-7)"
        }
    },
    subTitle: {
        fontWeight: 400,
        marginTop: "0.5rem",
        marginBottom: "1.75rem",
        fontSize: "var(--fz-1)",
        lineHeight: "var(--lh-1)",
        "@media all and (max-width:37.4375em)": {
            paddingRight: "1.5rem",
            paddingLeft: "1.5rem",
            textAlign: 'center'
        },
        "@media all and (min-width:37.5em) and (max-width:63.9375em)": {
            maxWidth: '546px',
            marginBottom: "2.25rem"
        },
        "@media all and (min-width:64em) and (max-width:79.9375em)": {
            fontSize: "var(--fz-2)",
            lineHeight: "var(--lh-2)"
        },
        "@media all and (min-width:64em)": {
            marginBottom: "2.25rem",
            maxWidth: '720px'
        },
        "@media all and (min-width:80em)": {
            marginTop: "1rem"
        }
    },
    innerTitle: {
        marginBottom: "1.5rem",
        fontSize: "var(--fz-1)",
        lineHeight: "var(--lh-1)",
        "@media all and (min-width:48em) and (max-width:63.9375em)": {
            marginBottom: "1.75rem"
        },
        "@media all and (min-width:64em) and (max-width:79.9375em)": {
            marginBottom: "1.75rem",
            fontSize: "var(--fz-2)",
            lineHeight: "var(--lh-2)"
        },
        "@media all and (min-width:64em)": {
            textAlign: 'start',
            marginBottom: "2.5rem"
        }
    },
    innerWrapper: {
        paddingTop: "1.5rem",
        paddingBottom: "1.75rem",
        minHeight: '650px',
        "@media all and (min-width:37.5em) and (max-width:47.9375em)": {
            paddingTop: "2.5rem",
            paddingBottom: "2.5rem",
            paddingRight: "2.5rem",
            paddingLeft: "2.5rem"
        },
        "@media all and (min-width:48em) and (max-width:63.9375em)": {
            paddingTop: "3rem",
            paddingBottom: "3rem",
            paddingRight: "5.25rem",
            paddingLeft: "5.25rem",
            maxWidth: '648px'
        },
        "@media all and (min-width:37.5em) and (max-width:63.9375em)": {
            minHeight: '628px',
            textAlign: 'start'
        },
        "@media all and (min-width:64em) and (max-width:79.9375em)": {
            minHeight: '858px',
            maxWidth: '816px',
            paddingTop: "3.75rem",
            paddingBottom: "3.75rem",
            paddingRight: "5.25rem",
            paddingLeft: "5.25rem"
        },
        "@media all and (min-width:80em)": {
            minHeight: '908px'
        }
    },
    innerWrapperMobile: {
        "@media all and (max-width:37.4375em)": {
            marginBottom: "2.25rem"
        }
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: "1rem",
        "@media all and (min-width:37.5em)": {
            rowGap: "1.75rem"
        }
    },
    textareaWrapper: {
        width: '100%',
        position: 'relative',
        transitionProperty: 'border-color',
        transitionDuration: '0.2s',
        transitionTimingFunction: 'ease-in-out',
        borderBottomColor: "rgba(var(--c-neutral1200),0.2)",
        borderBottomWidth: "1px",
        borderBottomStyle: 'solid',
        paddingBottom: "calc(0.5rem - 1px)",
        borderInlineEndColor: "rgba(var(--c-neutral1200),0.2)",
        borderInlineEndWidth: "1px",
        borderInlineEndStyle: 'solid',
        paddingInlineEnd: "0.5rem",
        borderInlineStartColor: "rgba(var(--c-neutral1200),0.2)",
        borderInlineStartWidth: "1px",
        borderInlineStartStyle: 'solid',
        paddingInlineStart: "0.5rem",
        borderTopColor: "rgba(var(--c-neutral1200),0.2)",
        borderTopWidth: "1px",
        borderTopStyle: 'solid',
        paddingTop: "calc(0.5rem - 1px)",
        ':hover': {
            borderTopColor: "rgb(var(--c-neutral1200))",
            borderBottomColor: "rgb(var(--c-neutral1200))",
            borderInlineStartColor: "rgb(var(--c-neutral1200))",
            borderInlineEndColor: "rgb(var(--c-neutral1200))"
        },
        ':focus': {
            borderTopColor: "rgb(var(--c-neutral1200))",
            borderBottomColor: "rgb(var(--c-neutral1200))",
            borderInlineStartColor: "rgb(var(--c-neutral1200))",
            borderInlineEndColor: "rgb(var(--c-neutral1200))"
        }
    },
    textarea: {
        width: '100%',
        backgroundColor: "rgb(var(--c-contentBg))",
        zIndex: 1,
        height: "4.5rem",
        borderRadius: "clamp(0px, ((100vw - 4px) - 100%) * 9999, 0.1875rem) / 0.1875rem",
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        resize: 'none',
        outline: 'none',
        fontFamily: 'var(--font-brand),"Helvetica Neue",Helvetica,arial,tahoma,sans-serif',
        "@media all and (min-width:64em)": {
            height: "8.75rem"
        },
        "@media all and (min-width:102em)": {
            fontSize: "var(--fz--1)",
            lineHeight: "var(--lh--1)"
        }
    },
    textareaLabel: {
        position: 'absolute',
        backgroundColor: "rgb(var(--c-contentBg))",
        fontFamily: 'var(--font-brand),"Helvetica Neue",Helvetica,arial,tahoma,sans-serif',
        top: "0.5rem",
        right: "0.5rem",
        left: undefined,
        transitionDuration: '0.2s',
        transitionTimingFunction: 'ease-in-out',
        transitionProperty: 'all',
        "@media all and (min-width:80em)": {
            fontSize: "var(--fz--1)",
            lineHeight: "var(--lh--1)"
        }
    },
    raisedLabel: {
        top: 0,
        transform: 'translateY(-50%)',
        insetInlineStart: "0.5rem",
        paddingRight: "0.25rem",
        paddingLeft: "0.25rem",
        color: "rgb(var(--c-neutral900))",
        fontWeight: 700,
        fontSize: "var(--fz--2)",
        lineHeight: "var(--lh--2)",
        "@media all and (min-width:102em)": {
            fontSize: "var(--fz--3)",
            lineHeight: "var(--lh--3)"
        }
    },
    submitBtn: {
        alignSelf: 'center',
        "@media all and (min-width:37.5em) and (max-width:47.9375em)": {
            marginTop: "-0.25rem"
        }
    },
    displayNone: {
        display: 'none'
    },
    stageWrapper: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
    },
    successTitle: {
        marginTop: "1.25rem",
        marginBottom: "0.5rem",
        fontSize: "var(--fz-5)",
        lineHeight: "var(--lh-5)",
        "@media all and (min-width:64em)": {
            marginTop: "1.5rem",
            marginBottom: "0.75rem",
            fontSize: "var(--fz-5)",
            lineHeight: "var(--lh-5)"
        }
    },
    errorTitle: {
        marginTop: "1.25rem",
        marginBottom: "0.25rem",
        fontSize: "var(--fz-1)",
        lineHeight: "var(--lh-1)",
        "@media all and (min-width:64em)": {
            marginTop: "1.5rem",
            marginBottom: "0.75rem",
            fontSize: "var(--fz-3)",
            lineHeight: "var(--lh-3)"
        }
    },
    warnIcon: {
        fontSize: "1.5rem",
        alignSelf: 'flex-start',
        verticalAlign: '-0.1em',
        marginInlineEnd: "0.25rem",
        "@media all and (min-width:64em)": {
            fontSize: "2rem"
        }
    },
    succesSubTitle: {
        fontFamily: 'var(--font-brand),"Helvetica Neue",Helvetica,arial,tahoma,sans-serif',
        fontSize: "var(--fz-3)",
        lineHeight: "var(--lh-3)",
        "@media all and (min-width:64em)": {
            fontSize: "var(--fz-3)",
            lineHeight: "var(--lh-3)"
        }
    },
    errorSubTitle: {
        fontFamily: 'var(--font-brand),"Helvetica Neue",Helvetica,arial,tahoma,sans-serif',
        fontSize: "var(--fz-1)",
        lineHeight: "var(--lh-1)",
        "@media all and (min-width:64em)": {
            fontSize: "var(--fz-3)",
            lineHeight: "var(--lh-3)"
        }
    },
    stageInnerWrapper: {
        paddingTop: "3.75rem",
        paddingRight: "1.25rem",
        paddingLeft: "1.25rem",
        "@media all and (min-width:64em)": {
            paddingTop: "6.25rem",
            paddingRight: "15rem",
            paddingLeft: "15rem"
        }
    }
});
const fieldTexts = {
    fullName: {
        default: 'אנא הזינו שם מלא',
        error: 'שם מלא שהוזן אינו תקין'
    },
    organization: {
        default: 'אנא הזינו שם ארגון',
        error: 'שם הארגון שהוזן אינו תקין'
    },
    subscriptionsAmount: {
        default: 'אנא הזינו כמות מינויים',
        error: 'כמות המינויים שהוזנה אינה תקינה'
    }
};
const msPurchase = config.get('msPurchase');
const textByStage = {
    title: {
        success: 'תודה!',
        error: 'אופס, נראה שמשהו השתבש'
    },
    subTitle: {
        success: 'אנחנו על זה. שירות הלקוחות יחזור אליך להמשך התהליך',
        error: 'אפשר לנסות שוב, או ליצור קשר עם שירות הלקוחות בטלפון 5200*'
    }
};
const email = 'digital-english@haaretz.co.il';
function FormStage({ formStage }) {
    if (formStage === 'default') return null;
    const isSuccess = formStage === 'success';
    return /*#__PURE__*/ _jsxs("div", {
        className: s9(c.stageWrapper),
        children: [
            isSuccess ? /*#__PURE__*/ _jsx(SuccessImage, {}) : /*#__PURE__*/ _jsx(ErrorImage, {}),
            /*#__PURE__*/ _jsxs("h3", {
                className: s9(isSuccess ? c.successTitle : c.errorTitle),
                children: [
                    isSuccess ? null : /*#__PURE__*/ _jsx(Icon, {
                        icon: "warn",
                        variant: "danger",
                        styleExtend: [
                            c.warnIcon
                        ]
                    }),
                    textByStage.title[formStage]
                ]
            }),
            /*#__PURE__*/ _jsx("p", {
                className: s9(isSuccess ? c.succesSubTitle : c.errorSubTitle),
                children: textByStage.subTitle[formStage]
            }),
            null
        ]
    });
}
const lettersPattern = '^([a-zA-Z\\s]+|[א-ת\\s]+)$';
export default function PpGroupSubscriptionPage() {
    const textareaId = React.useId();
    const [isRaised, setIsRaised] = React.useState(false);
    const [isInvalidFullName, setIsInvalidFullName] = React.useState(false);
    const [isInvalidOrganization, setIsInvalidOrganization] = React.useState(false);
    const [isInvalidsubscriptionsAmount, setIsInvalidsubscriptionsAmount] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [formStage, setFormStage] = React.useState('default');
    const isDefaultStage = formStage === 'default';
    const biAction = useBi();
    const onSubmit = async (event)=>{
        event.preventDefault();
        const inputs = {
            fullName: event.currentTarget.elements.namedItem('fullName'),
            email: event.currentTarget.elements.namedItem('email'),
            phone: event.currentTarget.elements.namedItem('phone'),
            organization: event.currentTarget.elements.namedItem('organization'),
            subscriptionsAmount: event.currentTarget.elements.namedItem('subscriptionsAmount'),
            additionalDetails: event.currentTarget.elements.namedItem('additionalDetails')
        };
        if (inputs.fullName && inputs.email && inputs.phone && inputs.organization && inputs.subscriptionsAmount && inputs.additionalDetails) {
            setIsLoading(true);
            const fullNameValue = inputs.fullName.value.trim();
            const emailValue = inputs.email.value.trim();
            const phoneValue = inputs.phone.value.trim();
            const organizationValue = inputs.organization.value.trim();
            const subscriptionsAmountValue = inputs.subscriptionsAmount.value.trim();
            const additionalDetailsValue = inputs.additionalDetails.value.trim();
            biAction({
                action_id: 190,
                feature: 'b2b form',
                feature_type: 'Marketing',
                campaign_name: 'b2b',
                newsletter_email: emailValue
            });
            try {
                const res = await fetch(`${msPurchase}/helper/groupSubscription`, {
                    method: 'POST',
                    body: JSON.stringify({
                        fullName: fullNameValue,
                        email: emailValue,
                        phoneNumber: phoneValue,
                        organizationName: organizationValue,
                        subscriptionsAmount: subscriptionsAmountValue,
                        additionalDetails: additionalDetailsValue
                    }),
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });
                const data = await res.json();
                if (data.success) {
                    setFormStage('success');
                } else {
                    setFormStage('error');
                }
            } catch (error) {
                setFormStage('error');
            } finally{
                setIsLoading(false);
            }
        }
    };
    return /*#__PURE__*/ _jsxs(PpLandingPageLayout, {
        gradientKind: "light",
        styleExtend: [
            c.base
        ],
        titleStyleExtend: [
            c.title
        ],
        subTitleStyleExtend: [
            c.subTitle
        ],
        innerTitleStyleExtend: [
            c.innerTitle,
            !isDefaultStage && c.displayNone
        ],
        innerWrapperStyleExtend: [
            c.innerWrapper,
            isDefaultStage ? c.innerWrapperMobile : c.stageInnerWrapper
        ],
        title: 'לדעת יותר עם מינוי ארגוני לאתר הארץ',
        subTitle: 'גישה מלאה למגוון התכנים המעניינים, המעמיקים ומרחיבי האופקים של הארץ',
        innerTitle: 'מלאו את הפרטים ונחזור אליכם עם הצעה מיוחדת לארגונים ומוסדות חינוכיים',
        children: [
            isDefaultStage ? /*#__PURE__*/ _jsxs("form", {
                className: s9(c.form),
                onSubmit: onSubmit,
                children: [
                    /*#__PURE__*/ _jsx(TextField, {
                        name: "fullName",
                        required: true,
                        pattern: lettersPattern,
                        label: 'שם מלא',
                        onInput: ()=>setIsInvalidFullName(false),
                        onInvalid: (e)=>{
                            e.preventDefault();
                            setIsInvalidFullName(true);
                        },
                        isInvalid: isInvalidFullName,
                        description: isInvalidFullName ? fieldTexts.fullName.error : fieldTexts.fullName.default
                    }),
                    /*#__PURE__*/ _jsx(TextField, {
                        name: "organization",
                        required: true,
                        pattern: lettersPattern,
                        label: 'ארגון',
                        onInput: ()=>setIsInvalidOrganization(false),
                        onInvalid: (e)=>{
                            e.preventDefault();
                            setIsInvalidOrganization(true);
                        },
                        isInvalid: isInvalidOrganization,
                        description: isInvalidOrganization ? fieldTexts.organization.error : fieldTexts.organization.default
                    }),
                    /*#__PURE__*/ _jsx(EmailField, {
                        autoFocus: false
                    }),
                    /*#__PURE__*/ _jsx(PhoneField, {}),
                    /*#__PURE__*/ _jsx(TextField, {
                        name: "subscriptionsAmount",
                        required: true,
                        pattern: "^\\d+$",
                        label: 'כמות מינויים',
                        onInput: ()=>setIsInvalidsubscriptionsAmount(false),
                        onInvalid: (e)=>{
                            e.preventDefault();
                            setIsInvalidsubscriptionsAmount(true);
                        },
                        isInvalid: isInvalidsubscriptionsAmount,
                        description: isInvalidsubscriptionsAmount ? fieldTexts.subscriptionsAmount.error : fieldTexts.subscriptionsAmount.default
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: s9(c.textareaWrapper),
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                htmlFor: textareaId,
                                className: s9(c.textareaLabel, isRaised && c.raisedLabel),
                                children: 'פרטים נוספים'
                            }),
                            /*#__PURE__*/ _jsx("textarea", {
                                name: "additionalDetails",
                                id: textareaId,
                                className: s9(c.textarea),
                                onFocus: ()=>{
                                    setIsRaised(true);
                                },
                                onBlur: (e)=>{
                                    if (!e.target.value) {
                                        setIsRaised(false);
                                    }
                                }
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        styleExtend: [
                            c.submitBtn
                        ],
                        type: "submit",
                        ...isLoading ? {
                            state: 'busy',
                            busyNotice: 'טוען...'
                        } : {
                            state: 'auto'
                        },
                        children: 'שליחה'
                    })
                ]
            }) : null,
            /*#__PURE__*/ _jsx(FormStage, {
                formStage: formStage
            })
        ]
    });
}
const colorCssVariablesHtz = [
    "neutral1200",
    "contentBg",
    "neutral900"
];
const lineHeightCssVariablesHtz = [
    "--lh-5",
    "--lh-6",
    "--lh-8",
    "--lh-7",
    "--lh-1",
    "--lh-2",
    "--lh--1",
    "--lh--2",
    "--lh--3",
    "--lh-3"
];
const fontSizeCssVariablesHtz = [
    "--fz-5",
    "--fz-6",
    "--fz-8",
    "--fz-7",
    "--fz-1",
    "--fz-2",
    "--fz--1",
    "--fz--2",
    "--fz--3",
    "--fz-3"
];

//# sourceMappingURL=PpGroupSubscriptionPage.page.js.map